// EPSE Input Panel - Unified configuration component
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { 
  Upload, Sparkles, ChevronDown, ChevronUp, Zap, Battery, Settings, 
  HelpCircle, Sun, Moon, Snowflake, Flower2, Leaf, CloudSun, SunMedium, AlertTriangle,
  Power, PowerOff
} from 'lucide-react';
import { BESSConfig, SyntheticProfileConfig } from '@/types/epse';
import { EPSEDataUpload } from './EPSEDataUpload';
import { EPSEPeakPeriodsEditor } from './EPSEPeakPeriodsEditor';
import { useState, useMemo } from 'react';

interface EPSEInputPanelProps {
  profileMode: 'measured' | 'synthetic';
  onProfileModeChange: (mode: 'measured' | 'synthetic') => void;
  syntheticConfig: SyntheticProfileConfig;
  onSyntheticConfigChange: (config: SyntheticProfileConfig) => void;
  bessConfig: BESSConfig;
  onBessConfigChange: (config: BESSConfig) => void;
  dispatchMode: 'rule_based' | 'perfect_foresight';
  onDispatchModeChange: (mode: 'rule_based' | 'perfect_foresight') => void;
  selectedSeason: 'winter' | 'zomer';
  onSeasonChange: (season: 'winter' | 'zomer') => void;
  onDataUpload: (data: any, detectedUnit?: 'kW' | 'kWh') => Promise<void>;
  isUploading: boolean;
  quarterDataCount: number;
  annualKwh?: number;
  // NEW: Optional module controls
  bessEnabled: boolean;
  onBessEnabledChange: (enabled: boolean) => void;
  pvEnabled: boolean;
  onPvEnabledChange: (enabled: boolean) => void;
  unitOverride: 'auto' | 'kW' | 'kWh';
  onUnitOverrideChange: (unit: 'auto' | 'kW' | 'kWh') => void;
  detectedUnit?: 'kW' | 'kWh';
}

export function EPSEInputPanel({
  profileMode,
  onProfileModeChange,
  syntheticConfig,
  onSyntheticConfigChange,
  bessConfig,
  onBessConfigChange,
  dispatchMode,
  onDispatchModeChange,
  selectedSeason,
  onSeasonChange,
  onDataUpload,
  isUploading,
  quarterDataCount,
  annualKwh,
  bessEnabled,
  onBessEnabledChange,
  pvEnabled,
  onPvEnabledChange,
  unitOverride,
  onUnitOverrideChange,
  detectedUnit,
}: EPSEInputPanelProps) {
  const [openSections, setOpenSections] = useState({
    profile: true,
    pv: true,
    bess: true,
    simulation: true,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Helper functions for config updates
  const updateSyntheticConfig = <K extends keyof SyntheticProfileConfig>(
    key: K,
    value: SyntheticProfileConfig[K]
  ) => {
    onSyntheticConfigChange({ ...syntheticConfig, [key]: value });
  };

  const updateBessConfig = (key: keyof BESSConfig, value: number) => {
    onBessConfigChange({ ...bessConfig, [key]: value });
  };

  const updateSeasonDistribution = (
    season: keyof SyntheticProfileConfig['season_distribution'],
    value: number
  ) => {
    const newDistribution = { ...syntheticConfig.season_distribution, [season]: value / 100 };
    onSyntheticConfigChange({ ...syntheticConfig, season_distribution: newDistribution });
  };

  // Calculated values
  const totalSeasonPercent = useMemo(() => {
    return Object.values(syntheticConfig.season_distribution).reduce((a, b) => a + b, 0) * 100;
  }, [syntheticConfig.season_distribution]);

  const isSeasonValid = Math.abs(totalSeasonPercent - 100) < 1;

  const estimatedPeak = useMemo(() => {
    if (syntheticConfig.use_manual_peak) return syntheticConfig.peak_kw_manual;
    const dailyKwh = syntheticConfig.annual_kwh / 365;
    const meanKw = dailyKwh / 24;
    return Math.min(meanKw / 0.6, syntheticConfig.connection_kw_limit * 1.2);
  }, [syntheticConfig]);

  const baseLoadKw = estimatedPeak * syntheticConfig.base_load_ratio;
  const peakExceedsLimit = estimatedPeak > syntheticConfig.connection_kw_limit;

  // Get current section number based on profile mode
  const getSectionNumber = (section: 'profile' | 'pv' | 'bess' | 'simulation') => {
    if (profileMode === 'synthetic') {
      const order = ['profile', 'pv', 'bess', 'simulation'];
      return order.indexOf(section) + 2; // Start at 2 (after Data Bron)
    } else {
      const order = ['pv', 'bess', 'simulation'];
      return order.indexOf(section) + 2; // Start at 2 (after Data Bron)
    }
  };

  return (
    <div className="space-y-6">
      {/* Section 1: Data Bron */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            1. Data Bron
          </CardTitle>
          <CardDescription>
            Kies hoe het energieprofiel wordt bepaald
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={profileMode}
            onValueChange={(v) => onProfileModeChange(v as 'measured' | 'synthetic')}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div 
              className={`flex items-start space-x-3 border rounded-lg p-4 cursor-pointer transition-colors ${profileMode === 'measured' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
              onClick={() => onProfileModeChange('measured')}
            >
              <RadioGroupItem value="measured" id="measured" className="mt-1" />
              <Label htmlFor="measured" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2 font-medium">
                  <Upload className="h-4 w-4" />
                  Kwartierdata uploaden
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload echte meetdata voor nauwkeurige simulaties
                </p>
                {quarterDataCount > 0 && (
                  <Badge variant="secondary" className="mt-2">
                    {quarterDataCount.toLocaleString('nl-NL')} datapunten
                  </Badge>
                )}
              </Label>
            </div>
            
            <div 
              className={`flex items-start space-x-3 border rounded-lg p-4 cursor-pointer transition-colors ${profileMode === 'synthetic' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
              onClick={() => onProfileModeChange('synthetic')}
            >
              <RadioGroupItem value="synthetic" id="synthetic" className="mt-1" />
              <Label htmlFor="synthetic" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2 font-medium">
                  <Sparkles className="h-4 w-4" />
                  Synthetisch profiel
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Genereer profiel op basis van jaarverbruik en verdelingen
                </p>
              </Label>
            </div>
          </RadioGroup>

          {/* Data upload for measured mode */}
          {profileMode === 'measured' && (
            <div className="mt-4">
              <EPSEDataUpload 
                onUpload={onDataUpload}
                isUploading={isUploading}
                unitOverride={unitOverride}
                onUnitOverrideChange={onUnitOverrideChange}
                detectedUnit={detectedUnit}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Energieprofiel (only for synthetic) */}
      {profileMode === 'synthetic' && (
        <Collapsible open={openSections.profile} onOpenChange={() => toggleSection('profile')}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    2. Energieprofiel Configuratie
                  </CardTitle>
                  {openSections.profile ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
                <CardDescription>
                  Definieer het verbruikspatroon op basis van schattingen
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-6">
                {/* Basic inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="annual_kwh" className="flex items-center gap-2">
                      Jaarverbruik (kWh)
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Totaal verwacht elektriciteitsverbruik per jaar. Te vinden op uw energierekening.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <Input
                      id="annual_kwh"
                      type="number"
                      value={syntheticConfig.annual_kwh}
                      onChange={(e) => updateSyntheticConfig('annual_kwh', Number(e.target.value))}
                      min={0}
                      step={1000}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="connection_limit" className="flex items-center gap-2">
                      Contractgrens (kW)
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Maximaal vermogen dat u mag afnemen volgens uw netaansluiting contract.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <Input
                      id="connection_limit"
                      type="number"
                      value={syntheticConfig.connection_kw_limit}
                      onChange={(e) => updateSyntheticConfig('connection_kw_limit', Number(e.target.value))}
                      min={0}
                      step={10}
                    />
                  </div>
                </div>

                {/* Peak Power */}
                <div className="p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-amber-500" />
                      Piekvermogen
                    </Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Auto</span>
                      <Switch
                        checked={syntheticConfig.use_manual_peak}
                        onCheckedChange={(checked) => updateSyntheticConfig('use_manual_peak', checked)}
                      />
                      <span className="text-sm text-muted-foreground">Handmatig</span>
                    </div>
                  </div>
                  
                  {syntheticConfig.use_manual_peak ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={syntheticConfig.peak_kw_manual}
                        onChange={(e) => updateSyntheticConfig('peak_kw_manual', Number(e.target.value))}
                        min={10}
                        max={1000}
                        className="w-32"
                      />
                      <span className="text-sm">kW</span>
                      {peakExceedsLimit && (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {(estimatedPeak - syntheticConfig.connection_kw_limit).toFixed(0)} kW boven limiet
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Automatisch: <span className="font-medium">{estimatedPeak.toFixed(0)} kW</span>
                    </p>
                  )}
                </div>

                {/* Day/Night ratio */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      Dag/Nacht Verdeling
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Hoeveel van het dagelijks verbruik valt overdag (06:00-22:00) vs 's nachts.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant="outline"><Sun className="h-3 w-3 mr-1" />{(syntheticConfig.day_night_ratio * 100).toFixed(0)}%</Badge>
                      <Badge variant="secondary"><Moon className="h-3 w-3 mr-1" />{((1 - syntheticConfig.day_night_ratio) * 100).toFixed(0)}%</Badge>
                    </div>
                  </div>
                  <Slider
                    value={[syntheticConfig.day_night_ratio * 100]}
                    onValueChange={([v]) => updateSyntheticConfig('day_night_ratio', v / 100)}
                    min={50}
                    max={95}
                    step={1}
                  />
                </div>

                {/* Season distribution */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Seizoensverdeling</Label>
                    <Badge variant={isSeasonValid ? 'outline' : 'destructive'}>
                      Totaal: {totalSeasonPercent.toFixed(0)}%
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: 'winter', label: 'Winter', icon: Snowflake, color: 'text-blue-500' },
                      { key: 'lente', label: 'Lente', icon: Flower2, color: 'text-pink-500' },
                      { key: 'zomer', label: 'Zomer', icon: CloudSun, color: 'text-amber-500' },
                      { key: 'herfst', label: 'Herfst', icon: Leaf, color: 'text-orange-500' },
                    ].map(({ key, label, icon: Icon, color }) => (
                      <div key={key} className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className={`flex items-center gap-1 ${color}`}>
                            <Icon className="h-4 w-4" />
                            {label}
                          </span>
                          <span className="font-medium">
                            {(syntheticConfig.season_distribution[key as keyof typeof syntheticConfig.season_distribution] * 100).toFixed(0)}%
                          </span>
                        </div>
                        <Slider
                          value={[syntheticConfig.season_distribution[key as keyof typeof syntheticConfig.season_distribution] * 100]}
                          onValueChange={([v]) => updateSeasonDistribution(key as keyof typeof syntheticConfig.season_distribution, v)}
                          min={5}
                          max={50}
                          step={1}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Base load and weekend reduction */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="flex items-center gap-2">
                        Basisbelasting
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>Minimale constante belasting (koeling, servers, verlichting).</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </Label>
                      <span className="text-sm font-medium">{baseLoadKw.toFixed(0)} kW</span>
                    </div>
                    <Slider
                      value={[syntheticConfig.base_load_ratio * 100]}
                      onValueChange={([v]) => updateSyntheticConfig('base_load_ratio', v / 100)}
                      min={10}
                      max={50}
                      step={1}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label>Weekend Reductie</Label>
                      <span className="text-sm text-muted-foreground">{(syntheticConfig.weekend_reduction * 100).toFixed(0)}%</span>
                    </div>
                    <Slider
                      value={[syntheticConfig.weekend_reduction * 100]}
                      onValueChange={([v]) => updateSyntheticConfig('weekend_reduction', v / 100)}
                      min={0}
                      max={80}
                      step={1}
                    />
                  </div>
                </div>

                {/* Peak periods editor */}
                <EPSEPeakPeriodsEditor
                  periods={syntheticConfig.peak_periods}
                  onChange={(periods) => updateSyntheticConfig('peak_periods', periods)}
                  baseLoadKw={baseLoadKw}
                  peakKw={estimatedPeak}
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Section: Zonnepanelen (PV) - Optional */}
      <Collapsible open={openSections.pv && pvEnabled} onOpenChange={() => pvEnabled && toggleSection('pv')}>
        <Card className={!pvEnabled ? 'opacity-60' : ''}>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => pvEnabled && toggleSection('pv')}>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <SunMedium className="h-5 w-5 text-green-600" />
                {getSectionNumber('pv')}. Zonnepanelen (PV)
                {!pvEnabled && <Badge variant="secondary" className="ml-2">Uitgeschakeld</Badge>}
              </CardTitle>
              <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2">
                  <PowerOff className={`h-4 w-4 ${!pvEnabled ? 'text-muted-foreground' : 'text-muted-foreground/50'}`} />
                  <Switch
                    checked={pvEnabled}
                    onCheckedChange={onPvEnabledChange}
                  />
                  <Power className={`h-4 w-4 ${pvEnabled ? 'text-green-600' : 'text-muted-foreground/50'}`} />
                </div>
                {pvEnabled && (openSections.pv ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />)}
              </div>
            </div>
            <CardDescription>
              {pvEnabled 
                ? 'Configureer zonnepanelen voor eigen opwek in de simulatie'
                : 'Schakel in om PV productie mee te nemen in de simulatie'
              }
            </CardDescription>
          </CardHeader>
          {pvEnabled && (
            <CollapsibleContent>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Geïnstalleerde capaciteit</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={syntheticConfig.pv_capacity_kwp || 0}
                        onChange={(e) => updateSyntheticConfig('pv_capacity_kwp', Number(e.target.value))}
                        min={0}
                        step={10}
                        className="w-32"
                      />
                      <span className="text-sm text-muted-foreground">kWp</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Oriëntatie</Label>
                    <Select
                      value={syntheticConfig.pv_profile_type || 'none'}
                      onValueChange={(v: any) => updateSyntheticConfig('pv_profile_type', v)}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Geen PV / Uit data</SelectItem>
                        <SelectItem value="south">Zuid</SelectItem>
                        <SelectItem value="east_west">Oost-West</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {syntheticConfig.pv_capacity_kwp > 0 && syntheticConfig.pv_profile_type !== 'none' && (
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                    <p className="text-sm text-green-700 dark:text-green-400">
                      Geschatte jaarproductie: <span className="font-semibold">{Math.round(syntheticConfig.pv_capacity_kwp * 950).toLocaleString('nl-NL')} kWh/jaar</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          )}
        </Card>
      </Collapsible>

      {/* Section: BESS Configuration - Optional */}
      <Collapsible open={openSections.bess && bessEnabled} onOpenChange={() => bessEnabled && toggleSection('bess')}>
        <Card className={!bessEnabled ? 'opacity-60' : ''}>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => bessEnabled && toggleSection('bess')}>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Battery className="h-5 w-5" />
                {getSectionNumber('bess')}. Batterij (BESS)
                {!bessEnabled && <Badge variant="secondary" className="ml-2">Uitgeschakeld</Badge>}
              </CardTitle>
              <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2">
                  <PowerOff className={`h-4 w-4 ${!bessEnabled ? 'text-muted-foreground' : 'text-muted-foreground/50'}`} />
                  <Switch
                    checked={bessEnabled}
                    onCheckedChange={onBessEnabledChange}
                  />
                  <Power className={`h-4 w-4 ${bessEnabled ? 'text-primary' : 'text-muted-foreground/50'}`} />
                </div>
                {bessEnabled && (openSections.bess ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />)}
              </div>
            </div>
            <CardDescription>
              {bessEnabled 
                ? 'Stel de batterijsysteem parameters in voor peak shaving'
                : 'Schakel in om batterij simulatie toe te voegen'
              }
            </CardDescription>
          </CardHeader>
          {bessEnabled && (
            <CollapsibleContent>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      Capaciteit
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Totale energiecapaciteit van de batterij in kilowattuur.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={bessConfig.capacity_kwh}
                        onChange={(e) => updateBessConfig('capacity_kwh', Number(e.target.value))}
                        min={0}
                        step={10}
                      />
                      <span className="text-sm text-muted-foreground">kWh</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      Vermogen
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Maximaal laad-/ontlaadvermogen van de batterij.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={bessConfig.power_kw}
                        onChange={(e) => updateBessConfig('power_kw', Number(e.target.value))}
                        min={0}
                        step={5}
                      />
                      <span className="text-sm text-muted-foreground">kW</span>
                    </div>
                  </div>
                </div>

                {/* Efficiency sliders */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label>Laad efficiëntie</Label>
                      <span className="text-sm text-muted-foreground">{(bessConfig.efficiency_charge * 100).toFixed(0)}%</span>
                    </div>
                    <Slider
                      value={[bessConfig.efficiency_charge * 100]}
                      onValueChange={([v]) => updateBessConfig('efficiency_charge', v / 100)}
                      min={80}
                      max={100}
                      step={1}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label>Ontlaad efficiëntie</Label>
                      <span className="text-sm text-muted-foreground">{(bessConfig.efficiency_discharge * 100).toFixed(0)}%</span>
                    </div>
                    <Slider
                      value={[bessConfig.efficiency_discharge * 100]}
                      onValueChange={([v]) => updateBessConfig('efficiency_discharge', v / 100)}
                      min={80}
                      max={100}
                      step={1}
                    />
                  </div>
                </div>

                {/* SOC settings */}
                <div className="space-y-4">
                  <Label>SOC Bereik (State of Charge)</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Minimum</span>
                        <span className="text-muted-foreground">{(bessConfig.soc_min * 100).toFixed(0)}%</span>
                      </div>
                      <Slider
                        value={[bessConfig.soc_min * 100]}
                        onValueChange={([v]) => updateBessConfig('soc_min', v / 100)}
                        min={0}
                        max={50}
                        step={5}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Maximum</span>
                        <span className="text-muted-foreground">{(bessConfig.soc_max * 100).toFixed(0)}%</span>
                      </div>
                      <Slider
                        value={[bessConfig.soc_max * 100]}
                        onValueChange={([v]) => updateBessConfig('soc_max', v / 100)}
                        min={50}
                        max={100}
                        step={5}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Initieel</span>
                        <span className="text-muted-foreground">{(bessConfig.soc_initial * 100).toFixed(0)}%</span>
                      </div>
                      <Slider
                        value={[bessConfig.soc_initial * 100]}
                        onValueChange={([v]) => updateBessConfig('soc_initial', v / 100)}
                        min={0}
                        max={100}
                        step={5}
                      />
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="pt-4 border-t">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">C-rate</p>
                      <p className="font-medium">{bessConfig.capacity_kwh > 0 ? (bessConfig.power_kw / bessConfig.capacity_kwh).toFixed(2) : '-'}C</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Bruikbare capaciteit</p>
                      <p className="font-medium">{((bessConfig.soc_max - bessConfig.soc_min) * bessConfig.capacity_kwh).toFixed(0)} kWh</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Max ontladen/dag</p>
                      <p className="font-medium">{(bessConfig.power_kw * 24 * bessConfig.efficiency_discharge).toFixed(0)} kWh</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Cycli/dag (max)</p>
                      <p className="font-medium">{(bessConfig.power_kw * 24 / bessConfig.capacity_kwh / 2).toFixed(1)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          )}
        </Card>
      </Collapsible>

      {/* Section: Simulation Settings */}
      <Collapsible open={openSections.simulation} onOpenChange={() => toggleSection('simulation')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  {getSectionNumber('simulation')}. Simulatie Instellingen
                </CardTitle>
                {openSections.simulation ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Dispatch Strategie
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p><strong>Rule-Based:</strong> Batterij reageert op real-time belasting (realistisch).</p>
                          <p className="mt-1"><strong>Perfect Foresight:</strong> Batterij weet vooraf wat het verbruik wordt (ideaal scenario).</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <Select value={dispatchMode} onValueChange={(v: any) => onDispatchModeChange(v)} disabled={!bessEnabled}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rule_based">Rule-Based (Realistisch)</SelectItem>
                      <SelectItem value="perfect_foresight">Perfect Foresight (Ideaal)</SelectItem>
                    </SelectContent>
                  </Select>
                  {!bessEnabled && (
                    <p className="text-xs text-muted-foreground">Schakel BESS in om dispatch strategie te kiezen</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Preview Seizoen
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Welk seizoen wordt getoond in de live simulatie preview.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <Select value={selectedSeason} onValueChange={(v: any) => onSeasonChange(v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="winter">Winter (hoogste verbruik)</SelectItem>
                      <SelectItem value="zomer">Zomer (laagste verbruik)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
}
